import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface VoidDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
  /** PAID→VOID is owner-only and irreversible — surface the extra warning. */
  fromPaid?: boolean;
}

/** Void action dialog with a MANDATORY reason (F5.4/F5.7). */
export function VoidDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  fromPaid,
}: VoidDialogProps) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel
              data-testid="void-dialog"
              className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            >
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Void this document
              </Dialog.Title>
              <p className="mt-2 text-sm text-gray-500">
                {fromPaid
                  ? 'Voiding a PAID invoice is owner-only and permanent. Create a linked replacement invoice afterward.'
                  : 'Voiding is permanent. The document number is never reused (F4.5).'}
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Reason (required)
                </label>
                <textarea
                  data-testid="void-reason-input"
                  rows={3}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-red-500"
                />
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="void-confirm"
                  disabled={!trimmed || isLoading}
                  onClick={() => onConfirm(trimmed)}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {isLoading ? 'Voiding…' : 'Void document'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default VoidDialog;
